/**
 * Order status card — banner, header (number + type + live indicator + status badge),
 * shareable tracking link, and progress indicator.
 */

import { CheckCircle2, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui'
import type { OrderStatus, OrderType, TrackingStep } from '@/types/order'
import { OrderProgressIndicator } from './OrderProgressIndicator'

interface OrderStatusCardProps {
  orderNumber: string | null
  orderType: OrderType
  status: OrderStatus
  isErrorState: boolean
  isRealtime: boolean
  isPolling: boolean
  currentStepIndex: number
  steps: TrackingStep[]
  trackingCode: string | null | undefined
  linkCopied: boolean
  onCopyLink: () => void
}

export function OrderStatusCard({
  orderNumber,
  orderType,
  status,
  isErrorState,
  isRealtime,
  isPolling,
  currentStepIndex,
  steps,
  trackingCode,
  linkCopied,
  onCopyLink,
}: OrderStatusCardProps) {
  const { t } = useTranslation()

  return (
    <Card className="mb-6 overflow-hidden">
      {/* Success banner */}
      {currentStepIndex >= 0 && !isErrorState && (
        <div className="bg-green-50 border-b border-green-100 px-6 py-3">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">
              {status === 'completed' ? t('common:orderComplete') : t('common:orderConfirmed')}
            </span>
          </div>
        </div>
      )}

      {/* Error banner */}
      {isErrorState && (
        <div className="bg-red-50 border-b border-red-100 px-6 py-3">
          <div className="flex items-center gap-2 text-red-700">
            <span className="font-medium">
              {status === 'cancelled'
                ? t('common:orderCancelled')
                : t('common:somethingWentWrong')}
            </span>
          </div>
        </div>
      )}

      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-xl">
              {t('common:orderNumber', { number: orderNumber || 'N/A' })}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
              {orderType === 'delivery' ? t('common:delivery') : t('common:pickup')}
              {(isRealtime || isPolling) && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/60">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isRealtime ? 'bg-green-500' : 'bg-yellow-500'
                    } animate-pulse`}
                  />
                  {isRealtime ? t('common:live') : t('common:updating')}
                </span>
              )}
            </p>
          </div>
          <Badge
            variant={isErrorState ? 'destructive' : 'secondary'}
            className="w-fit"
          >
            {status
              .split('-')
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ')}
          </Badge>
        </div>
      </CardHeader>

      {/* Share tracking link */}
      {trackingCode && (
        <div className="px-6 pb-3 border-t border-border pt-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground min-w-0 overflow-hidden">
              <span>{t('common:share')} </span>
              <span className="font-mono text-xs truncate block sm:inline">
                {window.location.origin}/tracking/{trackingCode}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={onCopyLink}
            >
              <Copy className="h-3.5 w-3.5" />
              {linkCopied ? t('common:copied') : t('common:copyLink')}
            </Button>
          </div>
        </div>
      )}

      <CardContent className="pt-4">
        {!isErrorState && (
          <OrderProgressIndicator
            steps={steps}
            currentStepIndex={currentStepIndex}
          />
        )}
      </CardContent>
    </Card>
  )
}
